import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

// ─── Monitor CRUD ───────────────────────────────────────────────

export function useSiteMonitors() {
  return useQuery({
    queryKey: ['site-monitors'],
    queryFn: () => apiClient.get('/site-monitors'),
  });
}

export function useSiteMonitor(id?: string) {
  return useQuery({
    queryKey: ['site-monitors', id],
    queryFn: () => apiClient.get(`/site-monitors/${id}`),
    enabled: !!id,
  });
}

export function useProjectSiteMonitor(projectId?: string) {
  return useQuery({
    queryKey: ['site-monitors', 'project', projectId],
    queryFn: () => apiClient.get(`/site-monitors/project/${projectId}`),
    enabled: !!projectId,
  });
}

export function useCreateSiteMonitor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { projectId: string; monitoredUrl: string; stripeSubscriptionId?: string }) =>
      apiClient.post('/site-monitors', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-monitors'] });
    },
  });
}

export function useUpdateSiteMonitor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; monitoredUrl?: string; isActive?: boolean; stripeSubscriptionId?: string }) =>
      apiClient.put(`/site-monitors/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-monitors'] });
    },
  });
}

export function useDeleteSiteMonitor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/site-monitors/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-monitors'] });
    },
  });
}

export function useRunMonitorChecks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => apiClient.post(`/site-monitors/project/${projectId}/run-checks`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-monitors'] });
    },
  });
}
